/**
 * Binary message decoding (server -> client)
 * Uses MessageRegistry pattern for extensible message handling.
 */

import {
  parseGenConfig,
  MSG_WELCOME,
  MSG_ROOM_INFO,
  MSG_SNAPSHOT,
  MSG_ERROR,
  MSG_PONG,
  MSG_VOXEL_BUILD_COMMIT,
  MSG_VOXEL_CHUNK_DATA,
  MSG_MAP_TILE_DATA,
  MSG_SURFACE_COLUMN_DATA,
  MSG_REQUEST_NACK,
  ByteReader,
  decodeSnapshot,
  decodeVoxelBuildCommit,
  decodeVoxelChunkData,
  decodeMapTileData,
  decodeSurfaceColumnData,
  decodeRequestNack,
  RoomSnapshot,
  VoxelBuildCommit,
  VoxelChunkData,
  BuildResult,
  buildResultToString,
  MapTileResponse,
  SurfaceColumnResponse,
  RequestNack,
} from '@worldify/shared';
import { useGameStore } from '../state/store';
import { registerHandler, dispatch } from './MessageRegistry';

// ============== Typed Event System ==============

/** Event types and their payloads */
interface GameEvents {
  snapshot: RoomSnapshot;
  buildCommit: VoxelBuildCommit;
  chunkData: VoxelChunkData;
  mapTileData: MapTileResponse;
  surfaceColumnData: SurfaceColumnResponse;
  requestNack: RequestNack;
}

type EventCallback<T> = (data: T) => void;

/** Single registry for all game event callbacks */
const eventCallbacks: { [K in keyof GameEvents]?: EventCallback<GameEvents[K]> } = {};

/**
 * Register a callback for a game event.
 * Only one callback per event type (last one wins).
 */
export function on<K extends keyof GameEvents>(
  event: K,
  callback: EventCallback<GameEvents[K]>
): void {
  eventCallbacks[event] = callback as EventCallback<GameEvents[keyof GameEvents]>;
}

/** Emit an event to its registered callback */
function emit<K extends keyof GameEvents>(event: K, data: GameEvents[K]): void {
  const callback = eventCallbacks[event];
  if (callback) {
    (callback as EventCallback<GameEvents[K]>)(data);
  }
}

/**
 * Decode and dispatch an incoming binary message.
 * Delegates to registered handlers via MessageRegistry.
 */
export function decodeMessage(data: Uint8Array): void {
  dispatch(data);
}

function handleWelcome(reader: ByteReader): void {
  const playerId = reader.readUint16();
  // Read length-prefixed room ID
  const roomLength = reader.readUint8();
  const roomBytes: number[] = [];
  for (let i = 0; i < roomLength; i++) {
    roomBytes.push(reader.readUint8());
  }
  const roomId = String.fromCharCode(...roomBytes);
  useGameStore.getState().setRoomInfo(roomId, playerId);
  // Trailing world seed (newer servers): enables client-side far-LOD generation in server mode.
  // Absent on older servers → far rings stay off.
  if (reader.remaining >= 4) {
    useGameStore.getState().setServerWorldSeed(reader.readUint32());
  }
  // ...and then the server's generation config, so the far rings the CLIENT generates are the world
  // the SERVER is generating. Without it they were the engine defaults, which is only the same world
  // by coincidence — and stops being one the moment a server world is tuned.
  if (reader.remaining >= 4) {
    const len = reader.readUint32();
    if (len > 0 && reader.remaining >= len) {
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) bytes[i] = reader.readUint8();
      try {
        useGameStore.getState().setServerGenConfig(
          parseGenConfig(JSON.parse(new TextDecoder().decode(bytes)), 0));
      } catch {
        console.warn('[net] unreadable generation config in WELCOME — far rings use the defaults');
      }
    }
  }
}

function handleRoomInfo(reader: ByteReader): void {
  const playerCount = reader.readUint8();
  useGameStore.getState().setPlayerCount(playerCount);
}

function handleSnapshot(reader: ByteReader): void {
  const snapshot = decodeSnapshot(reader);
  
  // Update tick in store for debug display
  useGameStore.getState().setServerTick(snapshot.tick);
  
  // Notify game core of new snapshot
  emit('snapshot', snapshot);
}

function handleError(reader: ByteReader): void {
  const errorCode = reader.readUint8();
  console.error('Server error:', errorCode);
}

function handlePong(reader: ByteReader): void {
  const timestamp = reader.readUint32();
  const ping = Date.now() - timestamp;
  useGameStore.getState().setPing(ping);
}

function handleBuildCommit(reader: ByteReader): void {
  const commit = decodeVoxelBuildCommit(reader);
  
  if (commit.result === BuildResult.SUCCESS) {
    emit('buildCommit', commit);
  } else {
    console.warn(`[net] Build rejected: ${buildResultToString(commit.result)}`);
  }
}

function handleChunkData(reader: ByteReader): void {
  const chunkData = decodeVoxelChunkData(reader);
  emit('chunkData', chunkData);
}

function handleMapTileData(reader: ByteReader): void {
  const tileData = decodeMapTileData(reader);
  emit('mapTileData', tileData);
}

function handleSurfaceColumnData(reader: ByteReader): void {
  const columnData = decodeSurfaceColumnData(reader);
  emit('surfaceColumnData', columnData);
}

function handleRequestNack(reader: ByteReader): void {
  const nack = decodeRequestNack(reader);
  emit('requestNack', nack);
}

// Register all message handlers
registerHandler(MSG_WELCOME, handleWelcome);
registerHandler(MSG_ROOM_INFO, handleRoomInfo);
registerHandler(MSG_SNAPSHOT, handleSnapshot);
registerHandler(MSG_ERROR, handleError);
registerHandler(MSG_PONG, handlePong);
registerHandler(MSG_VOXEL_BUILD_COMMIT, handleBuildCommit);
registerHandler(MSG_VOXEL_CHUNK_DATA, handleChunkData);
registerHandler(MSG_MAP_TILE_DATA, handleMapTileData);
registerHandler(MSG_SURFACE_COLUMN_DATA, handleSurfaceColumnData);
registerHandler(MSG_REQUEST_NACK, handleRequestNack);
